import { useDispatch, useSelector } from 'react-redux';
import { Typography, TableCellProps } from '@mui/material';
import {
  LuiSelect,
  LuiMaterialTable,
  LuiSelectItemEvent,
  LuiMaterialTableHeadCellStyles,
  LuiChip
} from '@jda/lui-common-component-library-mui5';
import { MRT_Row, MRT_RowData } from 'material-react-table';
import { ISelect } from '../../../models/Roles/RolesContracts';
import { selectDimensionPolicyData } from '../../../store/role/policyDimension/dimensionSelector';
import {
  setHierarchiesData,
  updateDimensionFormError
} from '../../../store/role/policyDimension/dimensionActionCreator';
import { compareByProperty } from '../../../utility/commonUtility';
import {
  DROPDOWN_MAX_HEIGHT,
  HIERARCHY_TABLE_LABEL,
  HIERARCHY_ACCESS_CONTROL,
  AUTHORIZING,
  OPTIONAL,
  DISPLAY_NAME,
  SELECT_ALL
} from '../../../shared/PolicyDialog/constants';
import { useStyles } from '../../../shared/PolicyDialog/styles';
import LuiMultiSelectCustom from '../../../shared/LuiMultiSelectCustom';
interface Data {
  hierarchyTableData: any;
  actionsList: any;
}
const HierarchyTable = ({ hierarchyTableData, actionsList }: Data) => {
  const dimensionPolicyData = useSelector(selectDimensionPolicyData);
  const dispatch = useDispatch();
  const selectAll = [{ id: '*', label: SELECT_ALL }];
  const { classes } = useStyles();

  const handleActionChange = (name: string, hierarchyId: string, e: ISelect[]) => {
    dispatch(setHierarchiesData(name, hierarchyId, e));
    dispatch(updateDimensionFormError(name, ''));
  };

  const handleDropdownChange = (name: string, hierarchyId: string, e: LuiSelectItemEvent) => {
    dispatch(setHierarchiesData(name, hierarchyId, e.target.value));
    dispatch(updateDimensionFormError(name, ''));
  };

  const readColumns = [
    {
      header: HIERARCHY_TABLE_LABEL.HIERARCHY,
      accessorKey: 'hierarchy',
      muiTableHeadCellProps: ({ column, table }) =>
        ({
          sx: theme => ({
            ...LuiMaterialTableHeadCellStyles({
              column,
              theme,
              table,
              styling: 'standard',
              virtualizationEnabled: false
            }),
            width: '33%'
          })
        } as TableCellProps),
      Cell: ({ row }: { row: MRT_Row<MRT_RowData> }) => {
        return (
          <Typography variant="body1" color="textPrimary" noWrap>
            {row.original.id}{' '}
            {row.original.useForAuthorization && <LuiChip label={AUTHORIZING} variant="outlined" size="small" />}
          </Typography>
        );
      }
    },
    {
      header: HIERARCHY_TABLE_LABEL.LEVELS_PERMISSIONS,
      accessorKey: 'levels',
      muiTableHeadCellProps: ({ column, table }) =>
        ({
          sx: theme => ({
            ...LuiMaterialTableHeadCellStyles({
              column,
              theme,
              table,
              styling: 'standard',
              virtualizationEnabled: false
            }),
            width: '33%'
          })
        } as TableCellProps),
      Cell: ({ row }: { row: MRT_Row<MRT_RowData> }) => {
        const selectedItemsForHierarchy =
          row.original.id in dimensionPolicyData?.hierarchyAllActions ||
          dimensionPolicyData?.hierarchyAllActions[row.original.id] !== undefined
            ? dimensionPolicyData?.hierarchyAllActions[row.original.id]['levels']
            : {};
        return (
          <LuiSelect
            maxHeight={DROPDOWN_MAX_HEIGHT}
            id="policy-rule-dropdown"
            onSelectionChange={e => handleDropdownChange('levels', row.original.id, e)}
            itemData={
              row.original?.levels
                ? [...selectAll, ...row.original?.levels].map(level => ({
                    ...level,
                    key: level.id,
                    displayName: level.label
                  }))
                : []
            }
            inputLabel={''}
            selectedItem={
              (selectedItemsForHierarchy?.id && {
                ...selectedItemsForHierarchy,
                key: selectedItemsForHierarchy.id,
                displayName: selectedItemsForHierarchy.label
              }) ||
              undefined
            }
            size="small"
            disabled={false}
          />
        );
      }
    },
    {
      header: HIERARCHY_TABLE_LABEL.ACTIONS,
      accessorKey: 'actions',
      muiTableHeadCellProps: ({ column, table }) =>
        ({
          sx: theme => ({
            ...LuiMaterialTableHeadCellStyles({
              column,
              theme,
              table,
              styling: 'standard',
              virtualizationEnabled: false
            }),
            width: '33%'
          })
        } as TableCellProps),
      Cell: ({ row }: { row: MRT_Row<MRT_RowData> }) => {
        return (
          <LuiMultiSelectCustom
            id="actions-multi-select"
            itemData={actionsList.sort(compareByProperty(DISPLAY_NAME))}
            itemKey="id"
            itemDisplayName="displayName"
            selectedItems={
              row.original.id in dimensionPolicyData?.hierarchyAllActions
                ? dimensionPolicyData?.hierarchyAllActions[row.original.id]['actions'] !== undefined
                  ? dimensionPolicyData?.hierarchyAllActions[row.original.id]['actions']
                  : []
                : []
            }
            selectAll
            inputLabelDisplay="hidden"
            className={classes.actionMultiSelect}
            selectAllDisplayText={SELECT_ALL}
            style={{ marginTop: 0 }}
            size="small"
            maxHeight={DROPDOWN_MAX_HEIGHT}
            onSelectionChange={(e: any) => handleActionChange('actions', row.original.id, e)}
            disabled={!(row.original.id in dimensionPolicyData?.hierarchyAllActions)}
          />
        );
      }
    }
  ];

  return (
    <>
      <Typography variant="h4" component="p" className={classes.hierarchyTypographyTitle}>
        {`${HIERARCHY_ACCESS_CONTROL} (${OPTIONAL})`}
      </Typography>
      <LuiMaterialTable
        data={hierarchyTableData}
        columns={readColumns}
        keyAccessor="id"
        hasFiltering={false}
        enableColumnActions={false}
        hasToolbar={false}
        loading={false}
        columnResizable={false}
        enableAlternateRowStyle={false}
        enableBottomToolbar={false}
      />
    </>
  );
};
export default HierarchyTable;
